import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Check, Loader2, LogOut, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("eg_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [sendingId, setSendingId] = useState("");

  const loadBookings = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${t || token}` },
      });
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("eg_admin_token");
        setToken("");
      } else {
        setError("Buchungen konnten nicht geladen werden.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) loadBookings();
  }, [token, loadBookings]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("eg_admin_token", data.token);
      setToken(data.token);
      setPassword("");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setError(typeof d === "string" ? d : "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const markPaid = async (id) => {
    setSendingId(id);
    setError("");
    try {
      await axios.post(`${API}/admin/bookings/${id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadBookings();
    } catch {
      setError("Markieren fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setSendingId("");
    }
  };

  const logout = () => {
    localStorage.removeItem("eg_admin_token");
    setToken("");
    setBookings(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-cream flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm rounded-md border border-line bg-shell p-10">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sand text-espresso">
            <Lock size={20} strokeWidth={1.6} />
          </span>
          <h1 className="mt-6 text-center font-serif text-3xl tracking-tight text-espresso">Buchungen</h1>
          <p className="mt-2 text-center text-sm font-light text-taupe">einfach glücklich · LOSLASSEN</p>
          <label className="mt-8 block">
            <span className="text-xs uppercase tracking-[0.24em] text-taupe">Passwort</span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-3 w-full border-b border-espresso/30 bg-transparent pb-3 text-base font-light text-ink outline-none transition-colors duration-300 focus:border-gold"
            />
          </label>
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-bronze">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-espresso px-6 py-3.5 text-sm font-medium tracking-wide text-cream transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Anmelden"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream px-6 py-12 md:px-14">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight text-espresso">Buchungen</h1>
            <p className="mt-1 text-sm font-light text-taupe">Online-Hypnomeditation LOSLASSEN · 03.09.2026</p>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-espresso/30 px-4 py-2 text-xs font-medium text-espresso transition-colors duration-300 hover:bg-espresso hover:text-cream"
          >
            <LogOut size={14} /> Abmelden
          </button>
        </div>

        {error && <p data-testid="admin-error" className="mt-6 text-sm text-bronze">{error}</p>}

        <div className="mt-10 space-y-4">
          {bookings === null ? (
            <p className="text-sm font-light text-taupe flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Lade Buchungen …</p>
          ) : bookings.length === 0 ? (
            <p data-testid="admin-empty" className="text-sm font-light text-taupe">Noch keine Anmeldungen.</p>
          ) : (
            bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-booking-row-${b.id}`}
                className="flex flex-col gap-4 rounded-md border border-line bg-shell p-5 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <p className="font-medium text-espresso">{b.name}</p>
                  <p className="text-sm font-light text-taupe">{b.email}</p>
                  <p className="mt-1 text-xs font-light text-taupe">Angemeldet: {formatDate(b.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  {b.paid ? (
                    <span data-testid={`paid-badge-${b.id}`} className="flex items-center gap-2 rounded-full bg-gold/20 px-4 py-2 text-xs font-medium text-espresso">
                      <Check size={14} /> Bezahlt · Zoom-E-Mail gesendet
                    </span>
                  ) : (
                    <button
                      data-testid={`mark-paid-button-${b.id}`}
                      onClick={() => markPaid(b.id)}
                      disabled={sendingId === b.id}
                      className="flex items-center gap-2 rounded-full bg-espresso px-5 py-2.5 text-xs font-medium text-cream transition-transform duration-300 hover:scale-[1.03] active:scale-[0.97] disabled:opacity-70"
                    >
                      {sendingId === b.id ? <Loader2 size={14} className="animate-spin" /> : null}
                      Als bezahlt markieren
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
